import type { PricelistSearchHit, PricelistTable } from '@erp/rpc';

/**
 * In-pricelist search shared by both CSV views. The two differ only in where the
 * matching happens: a pricelist small enough to be returned whole is already in
 * memory, so it filters here; a large one is scanned server-side and streams its
 * hits back. Match semantics are kept identical on purpose — literal text,
 * case-insensitive, tested against each cell rather than the joined row, so a
 * query never matches across a cell boundary.
 */

/** Hits for one table, with the headers needed to render them. */
export interface PricelistSearchGroup {
  /** Index into the pricelist's tables. */
  tableIndex: number;
  headers: string[];
  hits: PricelistSearchHit[];
}

/** Whether any cell of the row contains `needle` (already lower-cased). */
function rowMatches(row: Record<string, string>, needle: string): boolean {
  for (const value of Object.values(row)) {
    if (value.toLowerCase().includes(needle)) return true;
  }
  return false;
}

/**
 * Search tables already held in the browser, stopping at `maxHits` the same way
 * the server's scan does so both paths report "more matches" identically.
 */
export function findHitsInTables(
  tables: PricelistTable[],
  query: string,
  maxHits: number,
): { hits: PricelistSearchHit[]; truncated: boolean } {
  const needle = query.toLowerCase();
  const hits: PricelistSearchHit[] = [];

  for (const [tableIndex, table] of tables.entries()) {
    for (const [rowIndex, row] of table.rows.entries()) {
      if (!rowMatches(row, needle)) continue;
      if (hits.length >= maxHits) return { hits, truncated: true };
      hits.push({ table: tableIndex, rowIndex, row });
    }
  }
  return { hits, truncated: false };
}

/**
 * Hits grouped by their table, in table order, dropping any group whose table
 * the caller doesn't know about — a hit can only outrun the tables list if the
 * pricelist was re-extracted mid-search, and a missing header row is not
 * something to render around.
 */
export function groupHitsByTable(
  hits: PricelistSearchHit[],
  tables: { headers: string[] }[],
): PricelistSearchGroup[] {
  const byTable = new Map<number, PricelistSearchHit[]>();
  for (const hit of hits) {
    const group = byTable.get(hit.table);
    if (group) group.push(hit);
    else byTable.set(hit.table, [hit]);
  }

  return [...byTable.entries()]
    .sort(([left], [right]) => left - right)
    .flatMap(([tableIndex, tableHits]) => {
      const table = tables[tableIndex];
      if (!table) return [];
      return [{ tableIndex, headers: table.headers, hits: tableHits }];
    });
}

/** How many hits landed in each table, for the tab badges. */
export function countHitsByTable(
  hits: PricelistSearchHit[],
): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const hit of hits) {
    counts[hit.table] = (counts[hit.table] ?? 0) + 1;
  }
  return counts;
}
